// Practice mix grid - only areas that actually hold matters, grouped by
// tier. Click any tile to drill into the practice area.

import { Link } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { OTHER_AREA, areasWithMatters } from "../../data/practice-areas";
import { useFirmCounts } from "../../hooks/useFirmCounts";
import type { PracticeArea } from "../../data/types";

export function PracticeMixGrid() {
  const firm = useFirmCounts();

  if (!firm) {
    return (
      <div className="cc-card" style={{ padding: 18 }}>
        <SkeletonGroup label="Loading practice mix">
          <Skeleton width="30%" height={16} />
          <Skeleton width="100%" height={120} radius={10} />
        </SkeletonGroup>
      </div>
    );
  }

  const counts = firm.byPracticeArea;
  const areas = areasWithMatters(counts);
  const otherCount = counts.other ?? 0;
  const areaTotal = areas.length + (otherCount > 0 ? 1 : 0);

  const grouped: Array<{ tier: 1 | 2 | 3; items: PracticeArea[] }> = [1, 2, 3]
    .map((t) => ({
      tier: t as 1 | 2 | 3,
      items: areas
        .filter((p) => p.tier === t)
        .sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0)),
    }))
    .filter((g) => g.items.length > 0);

  if (areaTotal === 0) {
    return (
      <div className="cc-card">
        <div className="cc-card-head">
          <div className="cc-card-title">Practice mix</div>
        </div>
        <div style={{ padding: "18px", color: "var(--muted)", fontSize: 12.5 }}>
          No open matters in any practice area yet.
        </div>
      </div>
    );
  }

  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Practice mix</div>
        <div className="cc-card-sub">
          {areaTotal} area{areaTotal === 1 ? "" : "s"} with open matters
        </div>
      </div>
      <div style={{ padding: "4px 18px 18px" }}>
        {grouped.map((g) => (
          <div key={g.tier} style={{ marginTop: g.tier === 1 ? 4 : 14 }}>
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--muted)",
                margin: "8px 0 6px",
              }}
            >
              {g.tier === 1 ? "Primary" : g.tier === 2 ? "Regular" : "Occasional"} ·{" "}
              {g.items.length} area{g.items.length === 1 ? "" : "s"}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  g.tier === 3
                    ? "repeat(auto-fill, minmax(140px, 1fr))"
                    : "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 8,
              }}
            >
              {g.items.map((p) => {
                const count = counts[p.id] ?? 0;
                const tier = p.tier;
                return (
                  <Link
                    key={p.id}
                    to={`/practice/${p.id}`}
                    aria-label={`Open ${p.label} practice area`}
                    style={{
                      cursor: "pointer",
                      background: "var(--bg-elev)",
                      border: "1px solid var(--line)",
                      borderTop: `3px solid ${p.accent}`,
                      borderRadius: 8,
                      padding: tier === 3 ? "8px 10px" : "10px 12px",
                      opacity: tier === 3 ? 0.85 : 1,
                      transition: "transform 120ms, box-shadow 120ms",
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "var(--shadow-2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span className="pa-dot" style={{ background: p.accent }} />
                      <span
                        style={{
                          fontWeight: 600,
                          color: "var(--ink)",
                          fontSize: tier === 3 ? 11.5 : 12.5,
                        }}
                      >
                        {p.label}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <span
                        style={{
                          fontSize: tier === 3 ? 16 : tier === 2 ? 19 : 22,
                          fontWeight: 600,
                          letterSpacing: "-0.03em",
                          fontVariantNumeric: "tabular-nums",
                          color: "var(--ink)",
                          lineHeight: 1.1,
                        }}
                      >
                        {count}
                      </span>
                      <span
                        style={{
                          fontSize: 9.5,
                          color: "var(--muted)",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {count === 1 ? "matter" : "matters"}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 9.5,
                        color: "var(--muted)",
                        marginTop: 4,
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {p.heroMetricLabel}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        {otherCount > 0 && (
          <Link
            to={OTHER_AREA.to}
            style={{
              display: "block",
              marginTop: 12,
              fontSize: 11,
              color: "var(--muted)",
              textDecoration: "none",
            }}
          >
            + {otherCount} matter{otherCount === 1 ? "" : "s"} outside the configured areas
          </Link>
        )}
      </div>
    </div>
  );
}
